import PropTypes from 'prop-types';
import React, { forwardRef, useState, useEffect, useRef } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import usePrevious from 'use-previous';

import { ugcVideoShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import { SecondaryGhostButton, TransactionalSolidButton, SecondaryTextButton } from '@bibliocommons/base-buttons';
import { allowFreeText } from '@bibliocommons/utils-user';
import { videoUtils } from '@bibliocommons/utils-ugc';
import TextField from '@bibliocommons/text-field';
import TextArea from '@bibliocommons/text-area';
import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';
import { HelpLinkContainer } from 'app/components/shared/HelpLink';
import Panel from 'app/components/shared/Panel';

import './VideoEditor.scss';

const EDITOR_KEY = 'video';
const TITLE_CHARACTER_LIMIT = 50;
const DESC_CHARACTER_LIMIT = 500;
const URL_CHARACTER_LIMIT = 1000;

const VideoEditor = forwardRef(
  (
    {
      open,
      metadataId,
      currentUser,
      existingVideo,
      ugcEditorActions,
      videosTransactions,
      videosActions,
      handleCancel,
      handleSuccess
    },
    ref
  ) => {
    const [videoTitle, setVideoTitle] = useState(existingVideo?.get('title') || '');
    const [videoDescription, setVideoDescription] = useState(existingVideo?.get('description') || '');
    const [videoUrl, setVideoUrl] = useState(existingVideo?.get('url') || '');
    const [videoUrlError, setVideoUrlError] = useState(null);

    const prevOpen = usePrevious(open);
    const addButtonRef = useRef();
    const formRef = useRef();

    useEffect(() => {
      // Set focus on the form when it opens
      if (open && !prevOpen && formRef.current) {
        setFocusWithinElement(formRef.current);
      }

      // Set focus on the add button when the form is closed
      if (!open && prevOpen && addButtonRef.current) {
        setFocusWithinElement(addButtonRef.current);
      }
    }, [open, prevOpen, formRef, addButtonRef]);

    function handleOpen() {
      ugcEditorActions.openEditor(EDITOR_KEY);
    }

    function handleClose() {
      if (!existingVideo) ugcEditorActions.closeEditor(EDITOR_KEY);
      if (handleCancel) handleCancel();
    }

    function handleUrlChange(value) {
      setVideoUrl(value);
      setVideoUrlError(
        value && !videoUtils.urlToIFrameSrc(value) ? (
          <FormattedMessage
            id="invalid_video_url"
            values={{
              link: (...contents) => (
                <HelpLinkContainer showIcon={false} page="025ug/020video/">
                  {contents}
                </HelpLinkContainer>
              )
            }}
          />
        ) : null
      );
    }

    function isFormValid() {
      return videoUrl && !videoUrlError && videoTitle;
    }

    function saveVideo(e) {
      if (e) e.preventDefault();

      if (isFormValid()) {
        const payload = {
          metadataId,
          videoId: existingVideo?.get('id'),
          url: videoUrl,
          title: videoTitle,
          description: videoDescription,
          postSuccess: () => {
            if (!existingVideo) ugcEditorActions.closeEditor(EDITOR_KEY);
            if (handleSuccess) handleSuccess();
          }
        };

        if (payload.videoId) {
          videosActions.updateVideo(payload);
        } else {
          videosActions.addVideo(payload);
        }
      }
    }

    function renderCollapsed() {
      return (
        <SecondaryGhostButton ref={addButtonRef} dataKey="add-ugc" handleClick={handleOpen}>
          <FormattedMessage id="add_video" />
        </SecondaryGhostButton>
      );
    }

    function renderExpanded() {
      const isLoading = existingVideo
        ? videosTransactions.getIn([metadataId, existingVideo.get('id'), 'isUpdating'])
        : videosTransactions.getIn([metadataId, 'isAdding']);

      return (
        <Panel>
          <form ref={formRef} onSubmit={saveVideo} autoComplete="off" autoCorrect="off">
            <TextField
              value={videoUrl}
              onChange={handleUrlChange}
              errorMessage={videoUrlError}
              className="video-field"
              id={`${metadataId}-video-url-field`}
              info={<FormattedMessage id="video_url_info" />}
              label={<FormattedMessage id="video_url_label" />}
              maxLength={URL_CHARACTER_LIMIT}
              characterCountSrOnly
            />
            <TextField
              value={videoTitle}
              onChange={setVideoTitle}
              className="video-field"
              id={`${metadataId}-video-title-field`}
              label={<FormattedMessage id="title" />}
              maxLength={TITLE_CHARACTER_LIMIT}
            />
            <TextArea
              value={videoDescription}
              handleChange={setVideoDescription}
              className="video-field"
              id={`${metadataId}-video-description-field`}
              label={<FormattedMessage id="description" />}
              maxLength={DESC_CHARACTER_LIMIT}
              minHeight="6rem"
              fitToText
              optional
            />
            <div className="action-buttons">
              <SecondaryTextButton dataKey="cancel-button" type="button" handleClick={handleClose}>
                <FormattedMessage id="button_cancel" />
              </SecondaryTextButton>
              <TransactionalSolidButton
                handleClick={saveVideo}
                disabled={!isFormValid()}
                isLoading={isLoading}
                dataKey="post-ugc"
                type="submit"
              >
                <FormattedMessage id="post_video" />
              </TransactionalSolidButton>
            </div>
          </form>
        </Panel>
      );
    }

    if (!currentUser || allowFreeText(currentUser)) {
      return (
        <div className="cp-video-editor" ref={ref}>
          {open ? renderExpanded() : renderCollapsed()}
        </div>
      );
    }

    return null;
  }
);

VideoEditor.propTypes = {
  open: PropTypes.bool,
  existingVideo: ugcVideoShape,
  handleSuccess: PropTypes.func,
  handleCancel: PropTypes.func,
  currentUser: userIdentityShape,
  metadataId: PropTypes.string.isRequired,
  videosTransactions: ImmutablePropTypes.map.isRequired,
  videosActions: PropTypes.objectOf(PropTypes.func).isRequired,
  ugcEditorActions: PropTypes.objectOf(PropTypes.func).isRequired
};

export default VideoEditor;
